// Thin functional wrappers around `apiFetch` that bake in the path + method
// for each endpoint. These are what hooks and tests call — they make it easy
// to mock at a single boundary and keep the route strings centralised.

import { apiFetch, API_BASE } from './client';
import type {
  AdminUser,
  AuditFilters,
  AuditLogEntry,
  CalendarOut,
  Connection,
  EnabledProvider,
  Me,
  Page,
  ProviderConfig,
  ProviderConfigCreate,
  ProviderConfigPatch,
  ProviderType,
  SyncLogEntry,
  SyncNowAck,
  SyncRule,
  SyncRuleCreate,
  SyncRulePatch,
} from './types';

// ---- auth ----
export const getMe = () => apiFetch<Me>('/me');
export const postLogout = () => apiFetch<{ status: string }>('/auth/logout', { method: 'POST' });

/** Returns the absolute URL the browser should navigate to for OIDC login. */
export const loginRedirectUrl = (next: string) =>
  `${API_BASE}/auth/login?next=${encodeURIComponent(next)}`;

/** Returns the absolute URL the browser should navigate to for a provider OAuth round-trip. */
export const connectionAuthorizeUrl = (providerConfigId: number, next = '/connections') =>
  `${API_BASE}/connections/${providerConfigId}/authorize?next=${encodeURIComponent(next)}`;

// ---- providers ----
export const listProviders = () => apiFetch<EnabledProvider[]>('/providers');

// ---- connections ----
export const listConnections = () => apiFetch<Connection[]>('/connections');
export const deleteConnection = (id: number) =>
  apiFetch<void>(`/connections/${id}`, { method: 'DELETE' });
export const listCalendars = (connectionId: number) =>
  apiFetch<CalendarOut[]>(`/connections/${connectionId}/calendars`);
export const refreshCalendars = (connectionId: number) =>
  apiFetch<CalendarOut[]>(`/connections/${connectionId}/calendars/refresh`, { method: 'POST' });

// ---- rules ----
export const listRules = () => apiFetch<SyncRule[]>('/rules');
export const getRule = (id: number) => apiFetch<SyncRule>(`/rules/${id}`);
export const createRule = (payload: SyncRuleCreate) =>
  apiFetch<SyncRule>('/rules', { method: 'POST', body: payload });
export const updateRule = (id: number, payload: SyncRulePatch) =>
  apiFetch<SyncRule>(`/rules/${id}`, { method: 'PUT', body: payload });
export const deleteRule = (id: number) =>
  apiFetch<void>(`/rules/${id}`, { method: 'DELETE' });
export const pauseRule = (id: number) =>
  apiFetch<SyncRule>(`/rules/${id}/pause`, { method: 'POST' });
export const resumeRule = (id: number) =>
  apiFetch<SyncRule>(`/rules/${id}/resume`, { method: 'POST' });
export const syncNow = (id: number) =>
  apiFetch<SyncNowAck>(`/rules/${id}/sync-now`, { method: 'POST' });
export const ruleSyncLog = (id: number) =>
  apiFetch<SyncLogEntry[]>(`/rules/${id}/sync-log`);

// ---- admin: provider configs ----
export const listProviderConfigs = () =>
  apiFetch<ProviderConfig[]>('/admin/providers');
export const listProviderTypes = () =>
  apiFetch<ProviderType[]>('/admin/providers/types');
export const createProviderConfig = (payload: ProviderConfigCreate) =>
  apiFetch<ProviderConfig>('/admin/providers', { method: 'POST', body: payload });
export const updateProviderConfig = (id: number, payload: ProviderConfigPatch) =>
  apiFetch<ProviderConfig>(`/admin/providers/${id}`, { method: 'PUT', body: payload });
export const deleteProviderConfig = (id: number) =>
  apiFetch<void>(`/admin/providers/${id}`, { method: 'DELETE' });

// ---- admin: users ----
export const listAdminUsers = (limit = 100, offset = 0) =>
  apiFetch<Page<AdminUser>>('/admin/users', { query: { limit, offset } });

// ---- admin: audit ----
export const listAuditLog = (filters: AuditFilters = {}) =>
  apiFetch<Page<AuditLogEntry>>('/admin/audit', { query: filters });
